import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { ArrowLeft, ShieldCheck, Workflow, Database, ShieldOff, FileText, Edit3, Activity } from "lucide-react";
import { inventory, agriBandFor, tokenTrend, recentEvents } from "@/data/mockData";
import StatusPill from "@/components/aieco/StatusPill";
import Sparkline from "@/components/aieco/Sparkline";

const RISK_TONE = { Low: "success", Medium: "warning", High: "critical" };

const CTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#1A2238]/95 px-3 py-2 text-xs backdrop-blur-md">
      <div className="text-slate-400">{label}</div>
      <div className="font-mono text-slate-200">
        Tokens: <span style={{ color: "#5B8DEF" }}>{Number(payload[0].value).toLocaleString()}</span>
      </div>
    </div>
  );
};

export default function AgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agent = inventory.find((a) => a.id === agentId);

  if (!agent) {
    return (
      <div className="mx-auto max-w-[1480px] px-10 py-12 text-slate-300">
        <div className="text-sm text-slate-400">Agent not found.</div>
        <Link to="/inventory" className="mt-3 inline-flex items-center gap-1 text-[#5B8DEF]">
          <ArrowLeft size={14} /> Back to inventory
        </Link>
      </div>
    );
  }

  const band = agriBandFor(agent.agri);
  const agentEvents = recentEvents.filter((e) => e.agent.toLowerCase().includes(agent.name.split(" ")[0].toLowerCase()));

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid={`page-agent-detail-${agent.id}`}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <button onClick={() => navigate("/inventory")} data-testid="back-to-inventory" className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-200">
          <ArrowLeft size={13} /> Inventory
        </button>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/5 bg-gradient-to-br from-[#5B8DEF]/15 to-[#22C55E]/[0.06] text-[#5B8DEF]">
              <Workflow size={22} strokeWidth={1.6} />
            </div>
            <div>
              <h1 className="font-display text-3xl font-medium tracking-tight text-white">{agent.name}</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-400">{agent.purpose}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill tone={RISK_TONE[agent.risk]}>{agent.risk} risk</StatusPill>
            <Link
              to={`/inventory/${agent.id}/policy`}
              data-testid={`open-policy-editor-${agent.id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#5B8DEF] px-3 py-1.5 text-[12px] font-medium text-[#0b1020] hover:bg-[#7AA4F5]"
            >
              <Edit3 size={12} /> Edit policy
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Top stats */}
      <div className="mt-7 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="card-eco p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">AGRI™</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-mono text-3xl font-light text-white">{agent.agri}</span>
            <span className="text-[11px]" style={{ color: band.color }}>{band.label}</span>
          </div>
        </div>
        <div className="card-eco p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Tokens / day</div>
          <div className="mt-2 font-mono text-3xl font-light text-white">{agent.tokens}</div>
          <div className="mt-2"><Sparkline data={[60,72,68,80,90,85,100]} color="#5B8DEF" width={120} /></div>
        </div>
        <div className="card-eco p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Allowed actions</div>
          <div className="mt-2 font-mono text-3xl font-light text-white">{agent.allowedActions.length}</div>
        </div>
        <div className="card-eco p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Blocked fields</div>
          <div className="mt-2 font-mono text-3xl font-light text-white">{agent.blockedData.length}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        {/* Activity */}
        <div className="card-eco col-span-12 p-6 lg:col-span-7">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Token consumption · last 14d</div>
          <div className="mt-3 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={tokenTrend}>
                <defs>
                  <linearGradient id="agentTok" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5B8DEF" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#5B8DEF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="day" stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                <Tooltip content={<CTooltip />} />
                <Area type="monotone" dataKey="tokens" stroke="#5B8DEF" strokeWidth={1.8} fill="url(#agentTok)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Policy summary */}
        <div className="card-eco col-span-12 p-6 lg:col-span-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Active policy</div>
          <div className="mt-3 space-y-4 text-sm">
            <div>
              <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <Database size={12} /> Allowed data
              </div>
              <div className="flex flex-wrap gap-1.5">
                {agent.allowedData.map((x) => (
                  <span key={x} className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 font-mono text-[11px] text-emerald-300">{x}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <ShieldOff size={12} /> Blocked data
              </div>
              <div className="flex flex-wrap gap-1.5">
                {agent.blockedData.map((x) => (
                  <span key={x} className="rounded-md border border-red-500/25 bg-red-500/10 px-2 py-1 font-mono text-[11px] text-red-300">{x}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <FileText size={12} /> Rules
              </div>
              <ul className="space-y-1.5 text-[12px] text-slate-300">
                {agent.policies.map((p, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#5B8DEF]" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Recent events */}
        <div className="card-eco col-span-12 p-6">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Recent agent activity</div>
            <Link to="/audit" className="text-[11px] text-[#5B8DEF]">View full audit →</Link>
          </div>
          <ul className="space-y-2">
            {(agentEvents.length ? agentEvents : recentEvents.slice(0, 4)).map((e) => (
              <li key={e.id} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-[12px]">
                <div className="flex items-center gap-3">
                  <Activity size={13} className="text-[#5B8DEF]" />
                  <span className="text-slate-200">{e.title}</span>
                </div>
                <span className="font-mono text-[10px] text-slate-500">{e.agent} · {e.time}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
